import React, { useState } from 'react';
import {
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import DateSelector from '../components/dashboard/DateSelector';
import CurrencySelector from '../components/dashboard/CurrencySelector';
import ExportButton from '../components/dashboard/ExportButton';
import EarningsTrendChart from '../components/dashboard/EarningsTrendChart';
import NetworkComparisonChart from '../components/dashboard/NetworkComparisonChart';
import CurrencyBreakdownChart from '../components/dashboard/CurrencyBreakdownChart';
import RecentFTDsTable from '../components/dashboard/RecentFTDsTable';
import SyncStatusTable from '../components/dashboard/SyncStatusTable';
import AccountPerformanceTable from '../components/dashboard/AccountPerformanceTable';
import { useDashboardStore } from '../stores/dashboardStore';
import {
  useDashboardMetrics,
  useEarningsTrend,
  useNetworkPerformance,
  useCurrencyBreakdown,
  useRecentFTDs,
  useSyncStatus,
  useTriggerSync,
  useAccountPerformance,
} from '../hooks/useDashboardData';

const Dashboard: React.FC = () => {
  const { 
    filters, 
    setSelectedMonth, 
    setSelectedDate,
    setViewGranularity,
    setBaseCurrency,
    canViewDaily 
  } = useDashboardStore();
  const [syncingNetwork, setSyncingNetwork] = useState<string | null>(null);

  // Fetch data using React Query hooks
  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics(
    filters.timeRange,
    filters.baseCurrency,
    filters.selectedMonth,
    filters.selectedDate,
    filters.viewGranularity
  );
  const { data: earningsTrend, isLoading: trendLoading } = useEarningsTrend(
    filters.timeRange,
    filters.baseCurrency,
    filters.selectedMonth
  );
  const { data: networkPerformance, isLoading: networkLoading } = useNetworkPerformance(
    filters.timeRange
  );
  const { data: currencyBreakdown, isLoading: currencyLoading } = useCurrencyBreakdown(
    filters.timeRange
  );
  const { data: recentFTDs, isLoading: ftdsLoading } = useRecentFTDs();
  const { data: syncStatus, isLoading: syncLoading } = useSyncStatus();
  const { data: accountPerformance, isLoading: accountsLoading, error: accountsError } = useAccountPerformance(
    filters.timeRange,
    filters.selectedMonth,
    filters.viewGranularity,
    filters.selectedDate
  );
  const triggerSync = useTriggerSync();
  
  // Debug
  console.log('[Dashboard] accountPerformance:', accountPerformance);
  console.log('[Dashboard] accountsError:', accountsError);

  const handleExport = async () => {
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const csvContent = [
      ['Dashboard Export - Adwstats'],
      ['Period', metrics?.periodLabel || 'N/A'],
      [''],
      ['Monthly Summary'],
      ['Total Commission', accountPerformance?.summary?.totalCommission || 0],
      ['Revenue Share', accountPerformance?.summary?.totalRevenueShareCommission || 0],
      ['CPA Rewards', accountPerformance?.summary?.totalCpaCommission || 0],
      ['First Time Deposits', accountPerformance?.summary?.totalFtds || 0],
      ['Active Players', accountPerformance?.summary?.totalPlayers || 0],
      ['Total Deposits', accountPerformance?.summary?.totalDeposits || 0],
      ['Signups', accountPerformance?.summary?.totalSignups || 0],
      ['Account Count', accountPerformance?.summary?.accountCount || 0],
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `adwstats-${filters.selectedMonth || 'dashboard'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSync = async (networkId: string) => {
    setSyncingNetwork(networkId);
    try {
      await triggerSync.mutateAsync(networkId);
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncingNetwork(null);
    }
  };

  // Format currency
  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Determine the report title based on granularity
  const reportTitle = filters.viewGranularity === 'daily' ? 'Daily Report' : 'Monthly Report';
  const periodDescription = filters.viewGranularity === 'daily' 
    ? (filters.selectedDate ? new Date(filters.selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'Today')
    : (metrics?.periodLabel || 'Loading...');

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col gap-4 sm:gap-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                {reportTitle}
              </h1>
              <p className="text-zinc-400 mt-1 text-sm sm:text-base truncate">
                {periodDescription} • Performance Summary
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <CurrencySelector value={filters.baseCurrency} onChange={setBaseCurrency} />
              <ExportButton onExport={handleExport} />
            </div>
          </div>
          <DateSelector
            selectedMonth={filters.selectedMonth || `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
            selectedDate={filters.selectedDate}
            viewGranularity={filters.viewGranularity}
            onMonthChange={setSelectedMonth}
            onDateChange={setSelectedDate}
            onGranularityChange={setViewGranularity}
            canViewDaily={canViewDaily()}
          />
        </div>
      </div>

      {/* Summary Card */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="bg-gradient-to-br from-[hsl(225,15%,12%)] to-[hsl(225,15%,10%)] rounded-xl sm:rounded-2xl border border-[hsl(225,15%,18%)] p-4 sm:p-6 lg:p-8">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-white">
              {filters.viewGranularity === 'daily' ? 'Daily Summary' : 'Monthly Summary'}
            </h2>
            <span className="px-2 sm:px-3 py-1 bg-emerald-500/10 text-emerald-400 text-xs sm:text-sm font-medium rounded-full border border-emerald-500/20 hidden sm:inline-block">
              {periodDescription}
            </span>
          </div>
          
          {/* Hero metric: Commission */}
          <div className="mb-5 sm:mb-6">
            <p className="text-xs sm:text-sm text-zinc-500 mb-1">Total Commission</p>
            <p className="text-3xl sm:text-4xl font-bold text-emerald-400 tabular-nums">
              {accountsLoading ? '—' : formatCurrency(accountPerformance?.summary?.totalCommission || 0, filters.baseCurrency)}
            </p>
            <p className="text-xs text-zinc-500 mt-1">your earnings</p>
          </div>

          {/* Secondary metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            {/* FTDs */}
            <div>
              <p className="text-xs sm:text-sm text-zinc-500 mb-1">First Time Deposits</p>
              <p className="text-lg sm:text-2xl font-bold text-white tabular-nums">
                {accountsLoading ? '—' : (accountPerformance?.summary?.totalFtds || 0).toLocaleString()}
              </p>
              {metrics?.ftdsToday.comparison !== undefined && metrics.ftdsToday.comparison !== 0 && (
                <div className={`flex items-center gap-1 mt-1 text-xs ${metrics.ftdsToday.comparison > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {metrics.ftdsToday.comparison > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {metrics.ftdsToday.comparison > 0 ? '+' : ''}{metrics.ftdsToday.comparison} vs prev
                </div>
              )}
            </div>
            
            {/* Active Players */}
            <div>
              <p className="text-xs sm:text-sm text-zinc-500 mb-1">Active Players</p>
              <p className="text-lg sm:text-2xl font-bold text-white tabular-nums">
                {accountsLoading ? '—' : (accountPerformance?.summary?.totalPlayers || 0).toLocaleString()}
              </p>
            </div>
            
            {/* Deposits */}
            <div>
              <p className="text-xs sm:text-sm text-zinc-500 mb-1">Total Deposits</p>
              <p className="text-lg sm:text-2xl font-bold text-white tabular-nums">
                {accountsLoading ? '—' : formatCurrency(accountPerformance?.summary?.totalDeposits || 0, filters.baseCurrency)}
              </p>
            </div>
            
            {/* Registrations/Signups */}
            <div>
              <p className="text-xs sm:text-sm text-zinc-500 mb-1">Signups</p>
              <p className="text-lg sm:text-2xl font-bold text-white tabular-nums">
                {accountsLoading ? '—' : (accountPerformance?.summary?.totalSignups || 0).toLocaleString()}
              </p>
            </div>
          </div>
          
          {/* Commission Breakdown Section */}
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-[hsl(225,15%,18%)]">
            <h3 className="text-sm sm:text-md font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
              <Wallet className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-400" />
              Commission Breakdown
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {/* Total Commission */}
              <div className="bg-[hsl(225,15%,14%)] rounded-lg sm:rounded-xl p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-zinc-400 mb-1">Total Commission</p>
                <p className="text-base sm:text-xl lg:text-2xl font-bold text-emerald-400 tabular-nums">
                  {accountsLoading ? '—' : formatCurrency(accountPerformance?.summary?.totalCommission || 0, filters.baseCurrency)}
                </p>
                <p className="text-[10px] sm:text-xs text-zinc-500 mt-1">your total earnings</p>
              </div>
              
              {/* Revenue Share */}
              <div className="bg-[hsl(225,15%,14%)] rounded-lg sm:rounded-xl p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-zinc-400 mb-1">Revenue Share</p>
                <p className="text-base sm:text-xl lg:text-2xl font-bold text-blue-400 tabular-nums">
                  {accountsLoading ? '—' : formatCurrency(accountPerformance?.summary?.totalRevenueShareCommission || 0, filters.baseCurrency)}
                </p>
                <p className="text-[10px] sm:text-xs text-zinc-500 mt-1">% of player NGR</p>
              </div>
              
              {/* CPA */}
              <div className="bg-[hsl(225,15%,14%)] rounded-lg sm:rounded-xl p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-zinc-400 mb-1">CPA Rewards</p>
                <p className="text-base sm:text-xl lg:text-2xl font-bold text-purple-400 tabular-nums">
                  {accountsLoading ? '—' : formatCurrency(accountPerformance?.summary?.totalCpaCommission || 0, filters.baseCurrency)}
                </p>
                <p className="text-[10px] sm:text-xs text-zinc-500 mt-1">cost-per-acquisition</p>
              </div>
              
              {/* Unspecified */}
              <div className="bg-[hsl(225,15%,14%)] rounded-lg sm:rounded-xl p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-zinc-400 mb-1">Unspecified</p>
                <p className="text-base sm:text-xl lg:text-2xl font-bold text-yellow-400 tabular-nums">
                  {accountsLoading ? '—' : formatCurrency(
                    Math.max(0, (accountPerformance?.summary?.totalCommission || 0) - (accountPerformance?.summary?.totalRevenueShareCommission || 0) - (accountPerformance?.summary?.totalCpaCommission || 0)),
                    filters.baseCurrency
                  )}
                </p>
                <p className="text-[10px] sm:text-xs text-zinc-500 mt-1">not categorized</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Performance Table - Full Width */}
      <div className="mb-6 sm:mb-8 animate-fade-in stagger-5">
        <AccountPerformanceTable
          data={Array.isArray(accountPerformance?.data) ? accountPerformance.data : []}
          isLoading={accountsLoading}
          currency={filters.baseCurrency}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
        <div className="lg:col-span-2 animate-fade-in">
          <EarningsTrendChart
            data={earningsTrend || []}
            isLoading={trendLoading}
          />
        </div>
        <div className="animate-fade-in">
          <NetworkComparisonChart
            data={networkPerformance || []}
            isLoading={networkLoading}
          />
        </div>
        <div className="animate-fade-in">
          <CurrencyBreakdownChart
            data={currencyBreakdown || []}
            isLoading={currencyLoading}
          />
        </div>
      </div>

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
        <div className="animate-fade-in">
          <RecentFTDsTable data={recentFTDs || []} isLoading={ftdsLoading} />
        </div>
        <div className="animate-fade-in">
          <SyncStatusTable
            data={syncStatus || []}
            onSync={handleSync}
            isLoading={syncLoading}
            isSyncing={syncingNetwork}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
